import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemSecondaryAction,
  TextField,
  Tooltip,
  withStyles,
} from "@material-ui/core";
import { Delete, DragHandle, Edit } from "@material-ui/icons";
import format from "date-fns/format";
import React from "react";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import { SortableHandle } from "react-sortable-hoc";
import Component from "src/shared/components/Component";
import PartialCleanCheck from "src/shared/components/PartialCleanCheck";
import withAxios from "src/shared/components/withAxios";
import { ISeasonData, ISeasonResponse } from "src/api/types";
import { getAdminApi } from "../../seasonsPage/utils/getAdminApi/getAdminApi";
import { styles } from "./styles";
import { IProps, ISeasonsPageState } from "./types";

export default withStyles(styles)(
  withAxios(
    class extends Component<IProps, ISeasonsPageState> {
      public readonly state = this.buildState({
        seasonName: "",
        startDate: new Date(),
      });

      private textInput = React.createRef<HTMLInputElement>();

      private readonly handleSeasonNameChange = this.stateInputEventHandler(
        (v) => {
          return { seasonName: v };
        }
      );

      private partialCleanCheck: PartialCleanCheck<ISeasonsPageState> =
        new PartialCleanCheck(this, ["seasonName", "startDate"], (state) => {
          return {
            ...state,
            seasonName:
              state.seasonName !== undefined ? state.seasonName.trim() : "",
          };
        });

      public componentDidMount() {
        document.addEventListener("keydown", this.escFunction, false);
      }
      public componentWillUnmount() {
        document.removeEventListener("keydown", this.escFunction, false);
      }

      public render() {
        const { season } = this.props;
        const { editing } = this.state;
        return (
          <ListItem
            divider={true}
            key={season.id}
            className={this.props.classes.listItem}
            style={editing ? { backgroundColor: "#f0fbff" } : undefined}
          >
            <SortableDragHandle />
            {editing !== undefined && this.renderEditing(season)}
            {editing === undefined && this.renderNonEditing(season)}
          </ListItem>
        );
      }

      private renderNonEditing(season: ISeasonResponse) {
        const { openDeleteModal, classes } = this.props;
        const handleStartEditing = () => {
          if (this.state.editing === undefined) {
            const { season } = this.props;
            this.setState(
              (prevState) => {
                const newState: Partial<ISeasonsPageState> = {
                  editing: true,
                  seasonName: season.name,
                  startDate: new Date(season.startDate),
                };
                this.partialCleanCheck.setOldFormData(newState);
                return { ...prevState, ...newState };
              },
              () => {
                const node = this.textInput.current;
                if (node) {
                  node.focus();
                }
              }
            );
          }
        };

        return (
          <React.Fragment>
            <Grid
              className={classes.inputPadding}
              onClick={handleStartEditing}
              container={true}
              direction="row"
              spacing={2}
              alignItems="center"
            >
              <Grid item={true}>{season.name}</Grid>
              <Grid className={classes.nonEditingDate} item={true}>
                {format(season.startDate, "MMMM D")}
              </Grid>
            </Grid>
            <ListItemSecondaryAction>
              <Tooltip title="Edit" placement="left">
                <IconButton aria-label="Edit" onClick={handleStartEditing}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="left">
                <IconButton
                  aria-label="Delete"
                  onClick={() => openDeleteModal(season)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </React.Fragment>
        );
      }

      private renderEditing(season: ISeasonResponse) {
        const { classes } = this.props;
        const { seasonName, submitting, startDate } = this.state;

        const handleDayClick = (day: Date) => {
          this.setState({ startDate: day });
        };
        const handleOnFocus = () => {
          document.addEventListener("keydown", this.escFunction, false);
        };

        const handleOnBlur = () => {
          document.removeEventListener("keydown", this.escFunction, false);
        };

        const getCanSubmit = () => {
          const { submitting, seasonName } = this.state;
          return (
            Boolean(seasonName.trim()) &&
            submitting === undefined &&
            !this.partialCleanCheck.isClean()
          );
        };

        const handleStopEditing = () => {
          if (this.state.editing) {
            this.setState({ editing: undefined });
          }
        };

        return (
          <React.Fragment>
            <form className={classes.inputPadding} onSubmit={this.updateSeason}>
              <Grid
                container={true}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Grid item={true}>
                  <TextField
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    inputRef={this.textInput}
                    label={season.name}
                    value={seasonName}
                    onChange={this.handleSeasonNameChange}
                  />
                </Grid>
                <Grid item={true}>
                  <DayPicker
                    onDayClick={handleDayClick}
                    selectedDays={startDate}
                  />
                </Grid>
                <Grid>
                  <Button
                    type="submit"
                    disabled={!getCanSubmit()}
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleStopEditing}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
              {submitting !== undefined && <LinearProgress />}
            </form>
          </React.Fragment>
        );
      }

      private escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === 27) {
          this.setState({ editing: undefined });
        }
      };

      private updateSeason = async (e: React.FormEvent) => {
        e.preventDefault();
        this.runCancellableStatefulAsync(
          this,
          {
            submitting: true,
          },
          {
            editing: undefined,
            submitting: undefined,
          },
          async () => {
            const { season, setSeason, defaultSeasons } = this.props;
            const { seasonName, startDate } = this.state;
            const seasonEdit = {
              id: season.id,
              name: seasonName.trim(),
              order: season.order,
              schoolId: season.schoolId,
              startDate: Number(startDate),
            };
            await this.buildPut<ISeasonData, void>(
              this,
              `${getAdminApi(defaultSeasons)}season`
            )(seasonEdit);
            setSeason(season, seasonEdit.name, seasonEdit.startDate);
            this.props.loadSeasons();
          },
          () => {
            window.alert("Failed to update season");
            this.setState({ submitting: undefined });
          }
        );
      };
    }
  )
);

const SortableDragHandle = SortableHandle(() => {
  return <DragHandle aria-role="button" />;
});



// WEBPACK FOOTER //
// ./src/features/director/pages/seasons/components/season-list-item/SeasonListItem.tsx